'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { mockStores } from '@/lib/mockData';
import ShieldBadge from '@/components/ui/ShieldBadge';
import StarRating from '@/components/ui/StarRating';
import TrustScoreBar from '@/components/ui/TrustScoreBar';
import { MapPin, Phone, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';

const ITEMS_PER_PAGE = 9;

export default function StoreGrid() {
  const [page, setPage] = useState(1);
  const published = mockStores?.filter((s) => s?.published);
  const totalPages = Math.ceil(published?.length / ITEMS_PER_PAGE);
  const paginated = published?.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
        {paginated?.map((store) => (
          <Link
            key={store?.id}
            href="/store-detail-page"
            className="block bg-card rounded-2xl border border-border shadow-card card-hover overflow-hidden group"
          >
            {/* Store image */}
            <div className="relative h-44 bg-muted overflow-hidden">
              <AppImage
                src={store?.logo}
                alt={`${store?.name} store logo and storefront`}
                fill
                className="object-cover group-hover:scale-105 transition-transform duration-300"
                sizes="(max-width: 768px) 100vw, 33vw"
              />
              {store?.verified && (
                <div className="absolute top-3 left-3">
                  <ShieldBadge trustScore={store?.trustScore} verificationDate={store?.verificationDate} size="sm" />
                </div>
              )}
              <div className="absolute top-3 right-3 bg-black/60 text-white text-xs font-semibold px-2 py-1 rounded-lg">
                {store?.category}
              </div>
            </div>

            {/* Content */}
            <div className="p-4">
              <h3 className="font-semibold text-foreground text-base mb-1 truncate group-hover:text-primary transition-colors">
                {store?.name}
              </h3>

              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-3">
                <MapPin size={12} />
                <span>{store?.address?.split(',')?.[0]}, {store?.city}</span>
              </div>

              <p className="text-xs text-muted-foreground line-clamp-2 mb-3">
                {store?.description}
              </p>

              {/* Rating row */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <StarRating rating={store?.averageRating} showValue />
                  <span className="text-xs text-muted-foreground">
                    ({store?.reviews?.length} reviews)
                  </span>
                </div>
              </div>

              {/* Trust score */}
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-muted-foreground">Trust Score</span>
                  <span className="text-xs font-bold text-primary font-tabular">{store?.trustScore}/100</span>
                </div>
                <TrustScoreBar score={store?.trustScore} showLabel={false} />
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between pt-3 border-t border-border">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Phone size={11} />
                  <span className="font-tabular">{store?.phone?.replace('+91 ', '')}</span>
                </div>
                <span className="text-xs font-semibold text-primary flex items-center gap-1 group-hover:gap-2 transition-all">
                  View Details
                  <ExternalLink size={11} />
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>
      {/* Pagination */}
      <div className="flex items-center justify-between mt-10 pt-6 border-t border-border">
        <p className="text-sm text-muted-foreground">
          Showing <span className="font-semibold text-foreground">{(page - 1) * ITEMS_PER_PAGE + 1}–{Math.min(page * ITEMS_PER_PAGE, published?.length)}</span> of{' '}
          <span className="font-semibold text-foreground">{published?.length}</span> verified stores
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="p-2 rounded-lg border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-150 active:scale-95"
            aria-label="Previous page"
          >
            <ChevronLeft size={16} />
          </button>
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={`page-${i + 1}`}
              onClick={() => setPage(i + 1)}
              className={`w-9 h-9 rounded-lg text-sm font-semibold transition-all duration-150 active:scale-95
                ${page === i + 1
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'border border-border hover:bg-muted text-foreground'
                }`}
            >
              {i + 1}
            </button>
          ))}
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="p-2 rounded-lg border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-150 active:scale-95"
            aria-label="Next page"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}